import { createSlice } from '@reduxjs/toolkit';
import { isUndefined } from 'lodash';
// Direct path, not the utils barrel: that barrel reaches store/index and would
// close an import cycle back into this slice.
import { magicNumbers } from 'utils/magicNumbers';
import {
  applyFiltersToApiRequest,
  applyFiltersToAllPlatforms,
  setFiltersContext,
} from 'store/filter/filterSlice';
import {
  deleteAllSearchHistory,
  deleteSearchHistory,
  fetchQueries,
  fetchTrainingVideosSettings,
  getSearchHistory,
} from './actions';

/**
 * Free search that was never typed into is not a real selection. Any action
 * other than submitting a search puts the previously chosen subject back, so
 * no request goes out without a query and the picker shows the subject again,
 * as if free search had never been selected. Mutates the draft in place.
 */
const restoreSubjectFromEmptyFreeSearchState = (state) => {
  const { queryId, q, freeSearchFallbackQueryId } = state.currentQuery;
  const isEmptyFreeSearch = queryId === magicNumbers.YOUR_SEARCH_QUERY_ID && !q;
  if (!isEmptyFreeSearch || !freeSearchFallbackQueryId) return;

  state.currentQuery = {
    ...state.currentQuery,
    queryId: freeSearchFallbackQueryId,
    q: '',
    lastSearchQuery: '',
    freeSearchFallbackQueryId: 0,
  };
};

export const pageInitialState = {
  currentQuery: {
    lastQueryId: 0,
    lastSearchQuery: '',
    q: '',
    queryId: 0,
    resourceQuery: '',
    /**
     * Subject selected right before switching to free search. It is put back
     * as the selection if any action happens while free search is still empty.
     * Cleared once a real search is submitted.
     */
    freeSearchFallbackQueryId: 0,
  },
  logo: false,
  pageTitle: '',
  titleIcon: '',
  titleIconColor: '',
  titleIconSrc: '',
  menuKey: '',
  displayDateTimePicker: false,
  displayDatePicker: false,
  displayQuery: false,
  isSupportMenuVisible: false,
  dateTimePickerMinDate: null,
  dateTimePickerMaxDate: null,
  siteSettings: {},
  isSidebarMenuExpanded: false,
  isMobileSidebarMenuActive: false,
  lastSelectedPlatform: '',
  layoutProfileMenuStep: 'step_1',
  currentDate: {
    fromDate: undefined,
    toDate: undefined,
    date: undefined,
    timeDuration: '1w',
  },
  queries: {
    data: [],
    loading: true,
    error: null,
  },
  isQueriesSynced: false,
  trainingVideosSettings: {
    isContentsTrainingVideosViewed: false,
  },
  selectedRangePicker: 'defaultDurations',
  lastSelectedRangePicker: 'defaultDurations',
  daysAgo: 15,
  onlineStatus: true,
  searchHistory: {
    loading: false,
    data: [],
    error: null,
  },
  deleteSearchHistory: {
    loading: false,
    error: null,
  },
  deleteAllSearchHistory: {
    loading: false,
    error: null,
  },
};
export const pageSlice = createSlice({
  name: 'pageSlice',
  initialState: pageInitialState,
  reducers: {
    setCurrentPageConfig: (state, action) => {
      const config = action.payload;
      state.pageTitle = config.pageTitle;
      state.titleIcon = config.titleIcon || '';
      state.titleIconColor = config.titleIconColor || '';
      state.titleIconSrc = config.titleIconSrc || '';
      state.logo = config.logo || false;
      state.menuKey = config.menuKey || '';
      state.displayDateTimePicker = config.displayDateTimePicker || false;
      state.displayDatePicker = config.displayDatePicker || false;
      state.displayQuery = config.displayQuery || false;
      state.dateTimePickerMinDate = config.dateTimePickerMinDate || null;
      state.dateTimePickerMaxDate = config.dateTimePickerMaxDate || null;
      state.siteSettings = config.siteSettings;
    },
    appendCurrentPageConfig: (state, action) => {
      state = {
        ...state,
        ...action.payload,
      };
      return state;
    },
    toggleSidebarMenu: (state) => {
      state.isSidebarMenuExpanded = !state.isSidebarMenuExpanded;
    },
    toggleMobileSidebarMenu: (state, action) => {
      state.isMobileSidebarMenuActive = action.payload;
    },
    setLastSelectedRangePicker: (state, action) => {
      state.lastSelectedRangePicker = action.payload;
    },
    toggleSupportMenu: (state, action) => {
      if (!isUndefined(action.payload?.isVisible)) {
        state.isSupportMenuVisible = action.payload?.isVisible;
        return;
      }
      state.isSupportMenuVisible = !state.isSupportMenuVisible;
    },
    setCurrentQuery: (state, action) => {
      state.currentQuery = {
        ...state.currentQuery,
        ...action.payload,
      };
    },
    /**
     * Free search that was never typed into is not a real selection. Anything
     * other than submitting a search puts the previously chosen subject back,
     * so no request goes out without a query and the picker shows the subject
     * again, as if free search had never been selected.
     */
    restoreSubjectFromEmptyFreeSearch: (state) => {
      restoreSubjectFromEmptyFreeSearchState(state);
    },
    setCurrentDate: (state, action) => {
      state.currentDate = {
        ...state.currentDate,
        ...action.payload,
      };
    },
    setQueries: (state, action) => {
      state.queries.data = action.payload;
    },
    setLastSelectedPlatform: (state, action) => {
      state.lastSelectedPlatform = action.payload;
    },
    changeProfileMenuStep: (state, action) => {
      state.layoutProfileMenuStep = action.payload;
    },
    setSelectedRangePicker: (state, action) => {
      state.selectedRangePicker = action.payload;
    },
    setDaysAgo: (state, action) => {
      state.daysAgo = action.payload;
    },
    setOnlineStatus: (state, action) => {
      state.onlineStatus = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      /**
       * Filter, sort, date and refresh actions on the contents, statistics and
       * resources pages funnel through the two apply actions, while switching
       * platform tabs remounts a tab component, which always announces itself
       * with setFiltersContext. Handling them here restores the subject in the
       * same dispatch, before anything re-reads the state to build a request.
       */
      .addCase(applyFiltersToApiRequest, restoreSubjectFromEmptyFreeSearchState)
      .addCase(
        applyFiltersToAllPlatforms,
        restoreSubjectFromEmptyFreeSearchState
      )
      .addCase(setFiltersContext, restoreSubjectFromEmptyFreeSearchState)
      .addCase(fetchQueries.fulfilled, (state, action) => {
        state.queries.data = action.payload;
        state.queries.loading = false;
        state.queries.error = null;
        state.isQueriesSynced = true;
      })
      .addCase(fetchQueries.pending, (state) => {
        state.queries.loading = true;
        state.queries.error = null;
        state.isQueriesSynced = false;
      })
      .addCase(fetchQueries.rejected, (state, action) => {
        state.queries.loading = false;
        state.queries.error = action.error;
        state.isQueriesSynced = false;
      })
      .addCase(fetchTrainingVideosSettings.fulfilled, (state, action) => {
        state.trainingVideosSettings = action.payload;
      })
      .addCase(getSearchHistory.pending, (state) => {
        state.searchHistory.loading = true;
        state.searchHistory.error = null;
      })
      .addCase(getSearchHistory.rejected, (state, action) => {
        state.searchHistory.loading = false;
        state.searchHistory.error = action.payload;
      })
      .addCase(getSearchHistory.fulfilled, (state, action) => {
        state.searchHistory.data = action.payload;
        state.searchHistory.loading = false;
        state.searchHistory.error = null;
      })
      .addCase(deleteSearchHistory.pending, (state) => {
        state.deleteSearchHistory.loading = true;
        state.deleteSearchHistory.error = null;
      })
      .addCase(deleteSearchHistory.rejected, (state, action) => {
        state.deleteSearchHistory.loading = false;
        state.deleteSearchHistory.error = action.payload;
      })
      .addCase(deleteSearchHistory.fulfilled, (state) => {
        state.deleteSearchHistory.loading = false;
        state.deleteSearchHistory.error = null;
      })
      .addCase(deleteAllSearchHistory.pending, (state) => {
        state.deleteAllSearchHistory.loading = true;
        state.deleteAllSearchHistory.error = null;
      })
      .addCase(deleteAllSearchHistory.rejected, (state, action) => {
        state.deleteAllSearchHistory.loading = false;
        state.deleteAllSearchHistory.error = action.payload;
      })
      .addCase(deleteAllSearchHistory.fulfilled, (state) => {
        state.searchHistory.data = pageInitialState.searchHistory.data;
        state.deleteAllSearchHistory.loading = false;
        state.deleteAllSearchHistory.error = null;
      });
  },
});

export const {
  setCurrentPageConfig,
  toggleSidebarMenu,
  toggleMobileSidebarMenu,
  toggleSupportMenu,
  setCurrentQuery,
  restoreSubjectFromEmptyFreeSearch,
  setQueries,
  setCurrentDate,
  setLastSelectedPlatform,
  changeProfileMenuStep,
  setSelectedRangePicker,
  setLastSelectedRangePicker,
  setDaysAgo,
  appendCurrentPageConfig,
  setOnlineStatus,
} = pageSlice.actions;
